import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import './AboutUs.css';
import {
  FaGlobeAmericas,
  FaBalanceScale,
  FaHandsHelping,
  FaFistRaised,
  FaPeopleArrows,
  FaLandmark,
  FaFileContract,
  FaFileInvoice,
  FaFileInvoiceDollar,
  FaUsers,
  FaGlobe,
  FaDesktop,
  FaIdCard,
  FaBullseye,
  FaEye
} from 'react-icons/fa';
import Footer from '../footer/Footer';

const legalCompliance = [
  { title: 'Society Registration', description: 'Registered under the Societies Registration Act, 1860', Icon: FaLandmark },
  { title: 'Public Trust Registration', description: 'Registered under the Indian Trusts Act, 1882', Icon: FaFileContract },
  { title: '12A Certification', description: 'Registered under Income Tax Act, Section 12A', Icon: FaFileInvoice },
  { title: '80G Certification', description: 'Registered under Income Tax Act, Section 80G', Icon: FaFileInvoiceDollar },
  { title: 'CSR-1 Registration', description: 'Registered with MCA as CSR implementing agency', Icon: FaUsers },
  { title: 'FCRA Registration', description: 'Registered under FCRA to receive foreign contributions', Icon: FaGlobe },
  { title: 'NITI Aayog NGO Darpan ID', description: 'Registered on NITI Aayog NGO Darpan Portal', Icon: FaDesktop },
  { title: 'PAN & TAN Registration', description: 'PAN & TAN registered with Income Tax Department', Icon: FaIdCard },
];

// Our Values — each has its own accent color, used for the icon, title,
// underline, right-edge border and watermark on that card.
const ourValues = [
  { title: 'Inclusivity', description: 'Serving every community with fairness, dignity and respect.', Icon: FaGlobeAmericas, color: '#2E86F0' },
  { title: 'Compassion', description: 'Leading every action with empathy, kindness and care.', Icon: FaHandsHelping, color: '#E0245E' },
  { title: 'Collaboration', description: 'Strong partnerships that create lasting social impact.', Icon: FaPeopleArrows, color: '#F5991A' },
  { title: 'Empowerment', description: 'Equipping people with knowledge, opportunity and confidence.', Icon: FaFistRaised, color: '#7B3FE4' },
  { title: 'Sustainability', description: 'Caring for creation through responsible development.', Icon: FaGlobe, color: '#5BA300' },
  { title: 'Integrity', description: 'Acting with transparency, accountability and ethics.', Icon: FaBalanceScale, color: '#0FA3A3' },
];

const AboutUs = () => {
  return (<>
    <div>
      {/* Hero Section */}
      <section className="aboutus-hero-section">
        <div className="aboutus-hero-content">
          <h1>About HDSI</h1>
          <p>
            Learn more about the mission, vision, and values that drive us to create a positive
            impact in the world.
          </p>
          <Button variant="primary" href="#mission-section">Explore Our Work</Button>
        </div>
      </section>

      {/* Director's Quote */}
      <section className="aboutus-quote-section">
        <Container>
          <div className="aboutus-quote-box">
            <span className="aboutus-quote-mark">&ldquo;</span>
            <p className="aboutus-quote-text">
              What began in 2002 as a small step of faith has grown into a lifelong commitment to
              act justly, love compassion, and walk humbly alongside communities. HDSI exists to
              restore hope by serving people and caring for creation.
            </p>
            <p className="aboutus-quote-author">— Pramod Thorat, Director</p>
          </div>
        </Container>
      </section>

      {/* Main About Section */}
      <section id="mission-section" className="aboutus-section">
        <Container>

          {/* Mission & Vision */}
          <Row className="mt-4">
            <Col md={6} className="d-flex">
              <Card className="aboutus-card h-100 w-100">
                <Card.Body className="aboutus-card-body">
                  <div className="aboutus-mv-icon"><FaBullseye /></div>
                  <Card.Title className="aboutus-card-title">Our Mission</Card.Title>
                  <Card.Text className="aboutus-card-text">
                    To empower communities through inclusive development and collaborative action by advancing healthy ecosystems, sustainable livelihoods, and resilient communities through the 5J Framework.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6} className="d-flex">
              <Card className="aboutus-card h-100 w-100">
                <Card.Body className="aboutus-card-body">
                  <div className="aboutus-mv-icon"><FaEye /></div>
                  <Card.Title className="aboutus-card-title">Our Vision</Card.Title>
                  <Card.Text className="aboutus-card-text">
                    To build an equitable, resilient, and sustainable society where every person lives with dignity, justice, and hope, and all creation flourishes.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ===========================
              OUR VALUES — floating single-row cards
          =========================== */}
          <Row className="mt-5">
            <Col lg={12}>
              <section className="values-section">
                <div className="values-container">

                  <div className="values-heading">
                    <span className="values-eyebrow">What Guides Us</span>
                    <h2>Our Values</h2>
                    <p>Guided by compassion. Driven by integrity. Committed to lasting change.</p>
                  </div>

                  <div className="values-row">
                    <div className="values-track">
                      {[...ourValues, ...ourValues].map((value, index) => (
                        <div
                          className="value-card"
                          key={`${value.title}-${index}`}
                          aria-hidden={index >= ourValues.length ? 'true' : undefined}
                          style={{
                            '--card-color': value.color,
                            '--float-delay': `${(index % ourValues.length) * 0.4}s`,
                          }}
                        >
                          <div className="value-watermark">
                            <value.Icon color={value.color} size={64} />
                          </div>
                          <div className="value-icon">
                            <value.Icon color="#fff" size={26} />
                          </div>
                          <h4>{value.title}</h4>
                          <p>{value.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </section>
            </Col>
          </Row>

        </Container>
      </section>

      {/* Legal & Statutory Compliance */}
      <section className="aboutus-legal-section">
        <Container>
          <div className="aboutus-legal-heading">
            <h2>Legal &amp; Statutory Compliance</h2>
            <p>
              HDSI is a legally registered organization committed to{' '}
              <strong>transparency, accountability</strong> and <strong>responsible</strong> operations.
            </p>
          </div>

          <Row className="g-4">
            {legalCompliance.map((item) => (
              <Col md={3} sm={6} key={item.title}>
                <div className="aboutus-legal-card">
                  <span className="aboutus-legal-icon">
                    <item.Icon />
                  </span>
                  <h5>{item.title}</h5>
                  <p>{item.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

    </div>
    <Footer/></>
  );
};

export default AboutUs;